/**
 * Mermaid Tool - 主入口模組
 * static/js/mermaid/index.js
 * 
 * 整合所有模組並初始化應用程式
 */

// 導入模組
import { TEMPLATES, getDiagramIcon } from './config.js';
import { 
    getState, 
    updateState, 
    markUnsaved, 
    markSaved, 
    setCurrentDiagramId, 
    getCurrentDiagramId,
    hasUnsavedChanges 
} from './state.js';
import * as api from './api.js';
import { 
    showToast, 
    escapeHtml, 
    formatDate, 
    copyToClipboard, 
    showModal, 
    hideModal,
    updateRenderStatus,
    $,
    $$
} from './ui.js';
import { 
    initMermaid, 
    renderDiagram, 
    renderDiagramDebounced, 
    detectDiagramType,
    isFlowchartType,
    getFlowchartDirection,
    changeFlowchartDirection as changeDirection
} from './renderer.js';
import { 
    initCanvas, 
    initPanelResizer, 
    zoomToFit, 
    resetView 
} from './canvas.js';
import { 
    exportPng, 
    exportSvg, 
    copyCode, 
    getExportScale 
} from './export.js';
import { setupShareModal } from './share.js';

// DOM 元素參照
let codeEditor;
let previewArea;
let canvasContainer;
let canvasContent;
let diagramTitle;

// ============== 初始化 ==============

/**
 * 初始化應用程式
 */
function init() {
    // 獲取 DOM 元素
    codeEditor = $('#codeEditor');
    previewArea = $('#previewArea');
    canvasContainer = $('#canvasContainer');
    canvasContent = $('#canvasContent');
    diagramTitle = $('#diagramTitle');
    
    if (!codeEditor || !canvasContent) {
        console.error('Required DOM elements not found');
        return;
    }
    
    // 初始化 Mermaid
    initMermaid();
    
    // 初始化 Canvas 控制
    initCanvas(canvasContainer, canvasContent);
    
    // 初始化面板大小調整
    initPanelResizer();
    
    // 設定事件監聽
    setupEventListeners();
    
    // 設定分享 Modal
    setupShareModal();
    
    // 載入預設範本
    loadTemplate('flowchart');
    
    // 載入已儲存檔案列表
    loadSavedFilesList();
    
    // 視窗大小變更時重新適應
    let resizeTimeout;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            if (canvasContent?.querySelector('svg')) {
                zoomToFit();
            }
        }, 200);
    });
    
    // 離開頁面前警告
    window.addEventListener('beforeunload', (e) => {
        if (hasUnsavedChanges()) {
            e.preventDefault();
            e.returnValue = '';
        }
    });
    
    console.log('Mermaid Tool initialized');
}

// ============== 事件設定 ==============

/**
 * 設定所有事件監聯
 */
function setupEventListeners() {
    setupEditorEvents();
    setupTemplateEvents();
    setupFileEvents();
    setupExportEvents();
    setupDirectionEvents();
}

/**
 * 設定編輯器事件
 */
function setupEditorEvents() {
    // 程式碼變更時自動渲染
    codeEditor?.addEventListener('input', () => {
        markUnsaved();
        renderDiagramDebounced(codeEditor.value, canvasContent);
        updateDirectionDropdown();
    });
    
    // 標題變更
    diagramTitle?.addEventListener('input', () => {
        markUnsaved();
    });
}

/**
 * 設定範本事件
 */
function setupTemplateEvents() {
    // 範本按鈕
    $$('.template-btn[data-template]').forEach(btn => {
        btn.addEventListener('click', () => {
            const template = btn.dataset.template;
            if (template && TEMPLATES[template]) {
                loadTemplate(template);
            }
        });
    });
    
    // 下拉選單範本
    $$('.template-dropdown[data-template]').forEach(item => {
        item.addEventListener('click', (e) => {
            e.preventDefault();
            const template = item.dataset.template;
            if (template && TEMPLATES[template]) {
                loadTemplate(template);
            }
        });
    });
}

/**
 * 設定檔案事件
 */
function setupFileEvents() {
    // 新建
    $('#newBtn')?.addEventListener('click', newDiagram);
    
    // 儲存
    $('#saveBtn')?.addEventListener('click', saveDiagram);
    
    // 重新整理列表
    $('#refreshFileList')?.addEventListener('click', loadSavedFilesList);
    
    // 複製程式碼
    $('#copyCodeBtn')?.addEventListener('click', () => {
        copyCode(codeEditor?.value);
    });
}

/**
 * 設定匯出事件
 */
function setupExportEvents() {
    // PNG 匯出
    $('#exportPngBtn')?.addEventListener('click', () => {
        const title = diagramTitle?.value || '圖表';
        const scale = getExportScale();
        exportPng(canvasContent, title, scale);
    });
    
    // SVG 匯出
    $('#exportSvgBtn')?.addEventListener('click', () => {
        const title = diagramTitle?.value || '圖表';
        exportSvg(canvasContent, title);
    });
}

/**
 * 設定方向選擇事件
 */
function setupDirectionEvents() {
    $$('.direction-option').forEach(item => {
        item.addEventListener('click', (e) => {
            e.preventDefault();
            const direction = item.dataset.direction;
            if (direction) {
                changeFlowchartDirection(direction);
            }
        });
    });
}

// ============== 範本操作 ==============

/**
 * 載入範本
 * @param {string} templateName - 範本名稱
 */
function loadTemplate(templateName) {
    const template = TEMPLATES[templateName];
    if (!template) return;
    
    // 檢查未儲存變更
    if (hasUnsavedChanges()) {
        if (!confirm('有未儲存的變更，確定要載入新範本嗎？')) {
            return;
        }
    }
    
    // 重置狀態
    setCurrentDiagramId(null);
    markSaved();
    
    // 設定標題和程式碼
    if (diagramTitle) {
        diagramTitle.value = `新${template.name}`;
    }
    if (codeEditor) {
        codeEditor.value = template.code;
    }
    
    // 更新方向下拉選單
    updateDirectionDropdown();
    
    // 渲染
    renderDiagram(template.code, canvasContent).then(() => {
        setTimeout(zoomToFit, 100);
    });
}

// ============== 方向控制 ==============

/**
 * 更新方向下拉選單顯示
 */
function updateDirectionDropdown() {
    const code = codeEditor?.value || '';
    const dropdown = $('#directionDropdown');
    const label = $('#directionLabel');
    
    if (!dropdown) return;
    
    const isFlowchart = isFlowchartType(code);
    dropdown.style.display = isFlowchart ? 'inline-block' : 'none';
    
    if (isFlowchart && label) {
        const direction = getFlowchartDirection(code);
        if (direction) {
            label.textContent = direction;
        }
    }
}

/**
 * 變更流程圖方向
 * @param {string} newDirection - 新方向
 */
function changeFlowchartDirection(newDirection) {
    if (!codeEditor) return;
    
    const newCode = changeDirection(codeEditor.value, newDirection);
    if (newCode !== codeEditor.value) {
        codeEditor.value = newCode;
        markUnsaved();
        
        const label = $('#directionLabel');
        if (label) {
            label.textContent = newDirection;
        }
        
        renderDiagram(newCode, canvasContent);
        showToast(`方向已切換為 ${newDirection}`, 'success');
    }
}

// ============== 檔案操作 ==============

/**
 * 新建圖表
 */
function newDiagram() {
    if (hasUnsavedChanges()) {
        if (!confirm('有未儲存的變更，確定要新建嗎？')) {
            return;
        }
    }
    
    loadTemplate('flowchart');
    showToast('已建立新圖表', 'info');
}

/**
 * 儲存圖表
 */
async function saveDiagram() {
    const title = diagramTitle?.value?.trim();
    const code = codeEditor?.value?.trim();
    
    if (!title) {
        showToast('請輸入圖表標題', 'warning');
        diagramTitle?.focus();
        return;
    }
    
    if (!code) {
        showToast('請輸入圖表程式碼', 'warning');
        codeEditor?.focus();
        return;
    }
    
    const diagramType = detectDiagramType(code);
    const currentId = getCurrentDiagramId();
    
    try {
        if (currentId) {
            // 更新現有圖表
            await api.updateDiagram(currentId, {
                title,
                diagram_code: code,
                diagram_type: diagramType
            });
            showToast('圖表已更新', 'success');
        } else {
            // 建立新圖表
            const result = await api.saveDiagram({
                title,
                diagram_code: code,
                diagram_type: diagramType
            });
            setCurrentDiagramId(result.id);
            showToast('圖表已儲存', 'success');
        }
        
        markSaved();
        loadSavedFilesList();
        
    } catch (error) {
        showToast('儲存失敗: ' + error.message, 'danger');
    }
}

/**
 * 載入已儲存檔案列表
 */
async function loadSavedFilesList() {
    const listContainer = $('#savedFilesList');
    if (!listContainer) return;
    
    listContainer.innerHTML = `
        <div class="text-center py-3">
            <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
        </div>
    `;
    
    try {
        const result = await api.listDiagrams();
        const diagrams = result.diagrams || [];
        
        if (diagrams.length === 0) {
            listContainer.innerHTML = '<p class="text-muted small text-center py-3">尚無已儲存的圖表</p>';
            return;
        }
        
        let html = '';
        for (const diagram of diagrams) {
            const icon = getDiagramIcon(diagram.diagram_type);
            const isActive = diagram.id === getCurrentDiagramId() ? 'active' : '';
            
            html += `
                <div class="saved-file-item ${isActive}" data-id="${diagram.id}">
                    <div class="d-flex justify-content-between align-items-start">
                        <div class="file-title" title="${escapeHtml(diagram.title)}">
                            ${icon} ${escapeHtml(diagram.title)}
                        </div>
                        <button class="btn btn-sm btn-link text-danger p-0 delete-file-btn" data-id="${diagram.id}" title="刪除">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                    <div class="file-meta text-muted">
                        ${formatDate(diagram.updated_at || diagram.created_at)}
                    </div>
                </div>
            `;
        }
        
        listContainer.innerHTML = html;
        
        // 綁定點擊載入
        listContainer.querySelectorAll('.saved-file-item').forEach(item => {
            item.addEventListener('click', (e) => {
                if (e.target.closest('.delete-file-btn')) return;
                loadDiagram(parseInt(item.dataset.id, 10));
            });
        });
        
        // 綁定刪除
        listContainer.querySelectorAll('.delete-file-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                deleteDiagram(parseInt(btn.dataset.id, 10));
            });
        });
        
    } catch (error) {
        listContainer.innerHTML = '<p class="text-danger small text-center py-3">載入失敗</p>';
        console.error('Load saved files error:', error);
    }
}

/**
 * 載入圖表
 * @param {number} diagramId - 圖表 ID
 */
async function loadDiagram(diagramId) {
    if (hasUnsavedChanges()) {
        if (!confirm('有未儲存的變更，確定要載入其他圖表嗎？')) {
            return;
        }
    }
    
    try {
        const diagram = await api.getDiagram(diagramId);
        
        setCurrentDiagramId(diagram.id);
        
        if (diagramTitle) {
            diagramTitle.value = diagram.title;
        }
        if (codeEditor) {
            codeEditor.value = diagram.diagram_code;
        }
        
        markSaved();
        updateDirectionDropdown();
        
        await renderDiagram(diagram.diagram_code, canvasContent);
        setTimeout(zoomToFit, 100);
        
        // 更新列表中的 active 狀態
        loadSavedFilesList();
        
        showToast('已載入圖表', 'success');
        
    } catch (error) {
        showToast('載入失敗: ' + error.message, 'danger');
    }
}

/**
 * 刪除圖表
 * @param {number} diagramId - 圖表 ID
 */
async function deleteDiagram(diagramId) {
    if (!confirm('確定要刪除這個圖表嗎？')) {
        return;
    }
    
    try {
        await api.deleteDiagram(diagramId);
        
        // 如果刪除的是當前圖表，重置
        if (diagramId === getCurrentDiagramId()) {
            loadTemplate('flowchart');
        }
        
        loadSavedFilesList();
        showToast('圖表已刪除', 'success');
        
    } catch (error) {
        showToast('刪除失敗: ' + error.message, 'danger');
    }
}

// ============== DOM Ready ==============

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

// ============== 公開 API ==============

export {
    loadTemplate,
    newDiagram,
    saveDiagram,
    loadDiagram,
    loadSavedFilesList,
    changeFlowchartDirection
};
